import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import MatchBar from "../components/jobsiq/MatchBar";
import { Brain, Sparkle, VideoCamera, Briefcase, MapPin, ShareNetwork } from "@phosphor-icons/react";
import VerifiedBadge from "../components/jobsiq/VerifiedBadge";

export default function PublicReport() {
    const { token } = useParams();
    const [report, setReport] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get(`/public/report/${token}`)
            .then(({ data }) => setReport(data))
            .catch(() => setError("This report is no longer available"));
    }, [token]);

    if (error)
        return (
            <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="public-report-not-found">
                {error}
            </div>
        );
    if (!report)
        return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading report…</div>;

    const c = report.candidate || {};
    const md = report.match_detail || {};
    const va = report.video_analysis || null;

    return (
        <div className="min-h-screen bg-white" data-testid="public-report-page">
            <header className="border-b border-slate-200 sticky top-0 z-30 bg-white">
                <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-slate-950 flex items-center justify-center">
                            <Brain size={20} color="#fff" weight="duotone" />
                        </div>
                        <span className="font-display text-xl">
                            Jobs<span className="text-blue-600">IQ</span>
                        </span>
                    </Link>
                    <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5">
                        <ShareNetwork size={14} /> Public AI match report
                    </div>
                </div>
            </header>

            <section className="bg-slate-950 text-white relative overflow-hidden">
                <div className="absolute inset-0 grid-lines opacity-30" />
                <div className="relative max-w-[1200px] mx-auto px-6 py-12">
                    <div className="font-mono-plex text-xs uppercase tracking-[0.3em] text-blue-400 inline-flex items-center gap-1.5">
                        <Sparkle size={14} weight="fill" /> AI match report
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl mt-3 leading-tight">
                        {c.name} × {report.job_title}
                    </h1>
                    <div className="mt-3 text-slate-300 text-sm flex flex-wrap gap-4">
                        <span className="inline-flex items-center gap-1.5">
                            <Briefcase size={14} /> {report.company_name}
                        </span>
                        {c.location && (
                            <span className="inline-flex items-center gap-1.5">
                                <MapPin size={14} /> {c.location}
                            </span>
                        )}
                        {c.experience_years ? (
                            <span className="font-mono-plex">{c.experience_years}+ years experience</span>
                        ) : null}
                    </div>
                </div>
            </section>

            <section className="max-w-[1200px] mx-auto px-6 py-10 grid md:grid-cols-3 gap-6">
                <div className="md:col-span-2 space-y-6">
                    <div className="border border-slate-200 p-6">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                            <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500">
                                Overall match
                            </div>
                            <MatchBar score={report.match_score || 0} size="lg" />
                        </div>
                        <p className="mt-4 text-slate-800">{md.verdict || "—"}</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="border border-slate-200 p-6">
                            <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 mb-2">
                                Skill overlap
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {(md.skill_overlap || []).map((s) => (
                                    <span key={"o" + s} className="text-xs px-2 py-1 bg-blue-600 text-white font-mono-plex">
                                        {s}
                                    </span>
                                ))}
                                {(!md.skill_overlap || md.skill_overlap.length === 0) && (
                                    <span className="text-xs text-slate-400">—</span>
                                )}
                            </div>
                        </div>
                        <div className="border border-slate-200 p-6">
                            <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 mb-2">
                                Gaps
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {(md.missing_skills || []).map((s) => (
                                    <span key={"m" + s} className="text-xs px-2 py-1 border border-slate-300 text-slate-500 font-mono-plex line-through">
                                        {s}
                                    </span>
                                ))}
                                {(!md.missing_skills || md.missing_skills.length === 0) && (
                                    <span className="text-xs text-slate-400">No critical gaps detected.</span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="border border-slate-200 p-6">
                            <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 mb-2">Strengths</div>
                            <p className="text-slate-700 text-sm">{md.strengths || "—"}</p>
                        </div>
                        <div className="border border-slate-200 p-6">
                            <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 mb-2">Concerns</div>
                            <p className="text-slate-700 text-sm">{md.concerns || "—"}</p>
                        </div>
                    </div>

                    {va && (
                        <div className="border border-slate-200 p-6">
                            <div className="font-mono-plex text-xs uppercase tracking-widest text-blue-600 mb-3 inline-flex items-center gap-1.5">
                                <VideoCamera size={14} /> Video interview intelligence
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {[
                                    ["Overall", va.overall_score],
                                    ["Communication", va.communication],
                                    ["Clarity", va.clarity],
                                    ["Confidence", va.confidence],
                                    ["Engagement", va.engagement],
                                    ["Warmth", va.warmth],
                                    ["Composure", va.composure],
                                    ["Sentiment", va.sentiment],
                                ].filter(([, v]) => v !== undefined && v !== null && v !== "").map(([k, v]) => (
                                    <div key={k} className="border border-slate-200 p-3 text-center">
                                        <div className="font-display text-2xl">{v ?? "—"}</div>
                                        <div className="text-[10px] font-mono-plex uppercase tracking-widest text-slate-500">{k}</div>
                                    </div>
                                ))}
                            </div>
                            {va.emotional_cues?.length > 0 && (
                                <div className="mt-4 flex flex-wrap gap-1.5">
                                    {va.emotional_cues.map((c) => (
                                        <span key={c} className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 font-mono-plex">
                                            {c}
                                        </span>
                                    ))}
                                </div>
                            )}
                            {va.summary && <p className="text-sm text-slate-700 mt-3">{va.summary}</p>}
                        </div>
                    )}
                </div>

                <div className="space-y-6">
                    <Link
                        to={`/p/${c.user_id}`}
                        data-testid="view-public-profile"
                        className="block border border-slate-200 p-6 hover:border-slate-950"
                    >
                        <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 mb-1">Candidate</div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-display text-xl">{c.name}</div>
                            {c.is_verified && <VerifiedBadge testId="report-candidate-verified" />}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">{c.title || "—"}</div>
                        <div className="mt-3 flex flex-wrap gap-1">
                            {(c.skills || []).slice(0, 8).map((s) => (
                                <span key={s} className="text-[10px] px-1.5 py-0.5 border border-slate-300 font-mono-plex">
                                    {s}
                                </span>
                            ))}
                        </div>
                    </Link>
                    <Link
                        to="/register"
                        data-testid="hire-on-jobsiq-cta"
                        className="block border border-slate-200 p-6 hover:border-slate-950"
                    >
                        <div className="font-display text-xl">Generate yours</div>
                        <p className="text-sm text-slate-600 mt-1">
                            Apply with Jobs IQ and ship a public match report in one click.
                        </p>
                    </Link>
                </div>
            </section>
        </div>
    );
}
